import { addDays, addHours, startOfDay } from 'date-fns';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';
import { UI_DATE_FORMAT_WEEK_DAY } from '../../constants';
import type {
  TimelineCluster,
  TimelineDateRange,
  TimelineTick,
} from './timelineTypes';

/** Default marker size on the plot strip. */
export const TIMELINE_ICON_SIZE_PX = 16;
/** Horizontal overlap between markers in a cluster. */
export const TIMELINE_CLUSTER_OVERLAP_PX = 8;
/** Items closer than this (px) are treated as one clickable cluster. */
export const TIMELINE_CLUSTER_GAP_PX = TIMELINE_ICON_SIZE_PX;

const MS_HOUR = 60 * 60 * 1000;
const DAY_LABEL_MIN_PX = 80;
const HOUR_LABEL_MIN_PX = 40;

function farmLocalToUtcMs(localDate: Date, timeZone: string): number {
  return fromZonedTime(localDate, timeZone).getTime();
}

function eachFarmStep(
  startMs: number,
  endMs: number,
  timeZone: string,
  next: (local: Date) => Date,
  first: (startLocal: Date) => Date,
): number[] {
  const startLocal = toZonedTime(new Date(startMs), timeZone);
  const times: number[] = [];
  let cursor = first(startLocal);
  let guard = 0;

  while (guard < 400) {
    const utc = farmLocalToUtcMs(cursor, timeZone);
    if (utc > endMs) {
      break;
    }
    if (utc >= startMs) {
      times.push(utc);
    }
    cursor = next(cursor);
    guard += 1;
  }

  return times;
}

/**
 * Vector-like step from the visible window:
 * ≤ 36h → 2-hour labels
 * ≤ 3 days → 6-hour labels, plus midnight date labels
 * longer → farm-local midnight dates (`EEE dd/MM`), thinned to fit the width
 */
export function getTimelineTicks(
  startMs: number,
  endMs: number,
  width: number,
  timeZone: string,
): TimelineTick[] {
  if (width <= 0 || endMs <= startMs) {
    return [];
  }

  const durationHours = (endMs - startMs) / MS_HOUR;
  const tz = timeZone || 'UTC';

  if (durationHours <= 36) {
    return spaceLabeledTicks(
      eachFarmStep(
        startMs,
        endMs,
        tz,
        (local) => addHours(local, 2),
        startOfDay,
      ),
      startMs,
      endMs,
      width,
      HOUR_LABEL_MIN_PX,
      'HH:mm',
      'time',
    );
  }

  const midnights = spaceLabeledTicks(
    eachFarmStep(startMs, endMs, tz, (local) => addDays(local, 1), startOfDay),
    startMs,
    endMs,
    width,
    DAY_LABEL_MIN_PX,
    UI_DATE_FORMAT_WEEK_DAY,
    'date',
  );

  if (durationHours > 72) {
    return midnights;
  }

  const hours = spaceLabeledTicks(
    eachFarmStep(startMs, endMs, tz, (local) => addHours(local, 6), startOfDay),
    startMs,
    endMs,
    width,
    HOUR_LABEL_MIN_PX,
    'HH:mm',
    'time',
  );
  const midnightTimes = new Set(midnights.map((tick) => tick.timeMs));

  return [
    ...midnights,
    ...hours.filter((tick) => {
      if (midnightTimes.has(tick.timeMs)) {
        return false;
      }
      const x = timeToPlotX(tick.timeMs, startMs, endMs, width);
      return midnights.every(
        (midnight) =>
          Math.abs(timeToPlotX(midnight.timeMs, startMs, endMs, width) - x) >=
          HOUR_LABEL_MIN_PX,
      );
    }),
  ].sort((a, b) => a.timeMs - b.timeMs);
}

function spaceLabeledTicks(
  times: number[],
  startMs: number,
  endMs: number,
  width: number,
  minGapPx: number,
  format: string,
  kind: TimelineTick['kind'],
): TimelineTick[] {
  const ticks: TimelineTick[] = [];
  let lastX = Number.NEGATIVE_INFINITY;

  times.forEach((timeMs) => {
    const x = timeToPlotX(timeMs, startMs, endMs, width);
    if (x < -0.5 || x > width + 0.5) {
      return;
    }
    if (x - lastX < minGapPx) {
      return;
    }
    ticks.push({ timeMs, format, kind });
    lastX = x;
  });

  const endTime = times[times.length - 1];
  if (endTime !== undefined) {
    const endX = timeToPlotX(endTime, startMs, endMs, width);
    if (endX >= width - 0.5 && ticks[ticks.length - 1]?.timeMs !== endTime) {
      ticks.push({ timeMs: endTime, format, kind });
    }
  }

  return ticks;
}

export function timeToPlotX(
  timeMs: number,
  startMs: number,
  endMs: number,
  width: number,
): number {
  const duration = Math.max(endMs - startMs, 1);
  return ((timeMs - startMs) / duration) * width;
}

export function clusterTimelineItems<T>(
  items: T[],
  getTimeMs: (item: T) => number,
  startMs: number,
  endMs: number,
  width: number,
  gapPx = TIMELINE_CLUSTER_GAP_PX,
): TimelineCluster<T>[] {
  const inRange = items
    .filter((item) => {
      const time = getTimeMs(item);
      return time >= startMs && time <= endMs;
    })
    .sort((a, b) => getTimeMs(a) - getTimeMs(b));

  const clusters: TimelineCluster<T>[] = [];

  inRange.forEach((item) => {
    const x = timeToPlotX(getTimeMs(item), startMs, endMs, width);
    const last = clusters[clusters.length - 1];
    const lastRight =
      last === undefined
        ? Number.NEGATIVE_INFINITY
        : last.x + (last.items.length - 1) * TIMELINE_CLUSTER_OVERLAP_PX;

    if (last && x - lastRight <= gapPx) {
      last.items.push(item);
      return;
    }

    clusters.push({ items: [item], x });
  });

  return clusters;
}

export type ShiftTimelineDateRangeOptions = {
  nowMs?: number;
  /** Fraction of the current duration to slide. Defaults to 0.5. */
  stepRatio?: number;
  /** When true, forward shifts never end after `nowMs`. */
  clampToNow?: boolean;
};

/** Slide the window by `stepRatio` of its length. */
export function shiftTimelineDateRange(
  range: TimelineDateRange,
  direction: -1 | 1,
  options: ShiftTimelineDateRangeOptions = {},
): TimelineDateRange {
  const { nowMs, stepRatio = 0.5, clampToNow = false } = options;
  const startMs = Date.parse(range.startDate);
  const endMs = Date.parse(range.endDate);
  const duration = Math.max(endMs - startMs, 1);
  const step = duration * stepRatio;
  let nextStart = startMs + direction * step;
  let nextEnd = endMs + direction * step;

  if (clampToNow && nowMs !== undefined && direction === 1 && nextEnd > nowMs) {
    nextEnd = nowMs;
    nextStart = nowMs - duration;
  }

  return {
    startDate: new Date(nextStart).toISOString(),
    endDate: new Date(nextEnd).toISOString(),
  };
}

export function isDateRangeAtNow(
  range: TimelineDateRange,
  nowMs: number,
  slackMs = 60_000,
): boolean {
  return Date.parse(range.endDate) >= nowMs - slackMs;
}
